/*
  Copyright (C) 2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import {
  usePreset,
  updatePrimaryPalette,
  updateSurfacePalette,
} from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";
import Material from "@primeuix/themes/material";
import Lara from "@primeuix/themes/lara";
import Nora from "@primeuix/themes/nora";

/**
 * Theme presets available in PrimeVue v4
 */
const themePresets = {
  aura: Aura,
  material: Material,
  lara: Lara,
  nora: Nora,
};

/**
 * Custom surface palettes for colors not built into PrimeVue
 */
const customSurfacePalettes = {
  soho: {
    0: "#ffffff",
    50: "#f8f8f9",
    100: "#f0f1f2",
    200: "#e1e2e4",
    300: "#c4c5c8",
    400: "#9fa1a5",
    500: "#7f8084",
    600: "#65666a",
    700: "#4f5053",
    800: "#3a3b3d",
    900: "#27282a",
    950: "#131315",
  },
  viva: {
    0: "#ffffff",
    50: "#f9f9fa",
    100: "#f3f3f4",
    200: "#e6e7e8",
    300: "#cacacd",
    400: "#a5a6a9",
    500: "#87898a",
    600: "#6c6e6f",
    700: "#555658",
    800: "#3f4041",
    900: "#2a2b2c",
    950: "#151516",
  },
  ocean: {
    0: "#ffffff",
    50: "#f8f9f9",
    100: "#f1f2f2",
    200: "#e3e4e4",
    300: "#c7c8c8",
    400: "#a2a4a4",
    500: "#828787",
    600: "#686c6c",
    700: "#525555",
    800: "#3c3e3e",
    900: "#282a2a",
    950: "#141515",
  },
};

/**
 * Primary colors available for customization
 */
const primaryColors = [
  { name: "slate", label: "Slate", value: "#64748b" },
  { name: "emerald", label: "Emerald", value: "#10b981" },
  { name: "green", label: "Green", value: "#22c55e" },
  { name: "lime", label: "Lime", value: "#84cc16" },
  { name: "orange", label: "Orange", value: "#f97316" },
  { name: "amber", label: "Amber", value: "#f59e0b" },
  { name: "yellow", label: "Yellow", value: "#eab308" },
  { name: "teal", label: "Teal", value: "#14b8a6" },
  { name: "cyan", label: "Cyan", value: "#06b6d4" },
  { name: "sky", label: "Sky", value: "#0ea5e9" },
  { name: "blue", label: "Blue", value: "#3b82f6" },
  { name: "indigo", label: "Indigo", value: "#6366f1" },
  { name: "violet", label: "Violet", value: "#8b5cf6" },
  { name: "purple", label: "Purple", value: "#a855f7" },
  { name: "fuchsia", label: "Fuchsia", value: "#d946ef" },
  { name: "pink", label: "Pink", value: "#ec4899" },
  { name: "rose", label: "Rose", value: "#f43f5e" },
  { name: "red", label: "Red", value: "#ef4444" },
];

/**
 * Surface colors available for customization
 */
const surfaceColors = [
  { name: "slate", label: "Slate", value: "#64748b" },
  { name: "gray", label: "Gray", value: "#6b7280" },
  { name: "zinc", label: "Zinc", value: "#71717a" },
  { name: "neutral", label: "Neutral", value: "#737373" },
  { name: "stone", label: "Stone", value: "#78716c" },
  { name: "soho", label: "Soho", value: "#7f8084" },
  { name: "viva", label: "Viva", value: "#87898a" },
  { name: "ocean", label: "Ocean", value: "#828787" },
];

/**
 * Scale values for font size adjustment
 */
const scaleValues = [12, 13, 14, 15, 16, 17, 18];
const scalePercentages = ["85%", "90%", "95%", "100%", "105%", "110%", "115%"];

/**
 * Default theme settings
 */
const defaults = {
  darkMode: false,
  preset: "aura",
  primaryColor: "emerald",
  surfaceColor: "slate",
  scale: 3, // 15px = 100%
};

/**
 * Storage keys for theme settings
 */
const storageKeys = {
  darkMode: "user-dark-mode",
  preset: "user-preset",
  primaryColor: "user-primary-color",
  surfaceColor: "user-surface-color",
  scale: "user-scale",
};

/**
 * Composable for managing PrimeVue v4 theme settings
 * Supports dark mode, presets, primary/surface colors, and font scale
 */
export function useTheme() {
  /**
   * Apply dark mode setting
   */
  const applyDarkMode = () => {
    const isDarkMode = localStorage.getItem(storageKeys.darkMode) === "true";
    if (isDarkMode) {
      document.documentElement.classList.add("app-dark");
    } else {
      document.documentElement.classList.remove("app-dark");
    }
    return isDarkMode;
  };

  /**
   * Set dark mode and save to localStorage
   */
  const setDarkMode = (enabled) => {
    localStorage.setItem(storageKeys.darkMode, enabled.toString());
    if (enabled) {
      document.documentElement.classList.add("app-dark");
    } else {
      document.documentElement.classList.remove("app-dark");
    }
  };

  /**
   * Get current dark mode setting
   */
  const isDarkMode = () => {
    return localStorage.getItem(storageKeys.darkMode) === "true";
  };

  /**
   * Apply theme preset
   */
  const applyPreset = () => {
    const savedPreset =
      localStorage.getItem(storageKeys.preset) || defaults.preset;
    if (savedPreset && themePresets[savedPreset]) {
      usePreset(themePresets[savedPreset]);
    }
    return savedPreset;
  };

  /**
   * Set theme preset and save to localStorage
   */
  const setPreset = (presetName) => {
    if (themePresets[presetName]) {
      localStorage.setItem(storageKeys.preset, presetName);
      usePreset(themePresets[presetName]);
      // Reapply colors after preset change
      applyPrimaryColor();
      applySurfaceColor();
    }
  };

  /**
   * Get current preset name
   */
  const getPreset = () => {
    return localStorage.getItem(storageKeys.preset) || defaults.preset;
  };

  /**
   * Apply primary color palette
   */
  const applyPrimaryColor = () => {
    const savedColor =
      localStorage.getItem(storageKeys.primaryColor) || defaults.primaryColor;
    updatePrimaryPalette({
      50: `{${savedColor}.50}`,
      100: `{${savedColor}.100}`,
      200: `{${savedColor}.200}`,
      300: `{${savedColor}.300}`,
      400: `{${savedColor}.400}`,
      500: `{${savedColor}.500}`,
      600: `{${savedColor}.600}`,
      700: `{${savedColor}.700}`,
      800: `{${savedColor}.800}`,
      900: `{${savedColor}.900}`,
      950: `{${savedColor}.950}`,
    });
    return savedColor;
  };

  /**
   * Set primary color and save to localStorage
   */
  const setPrimaryColor = (colorName) => {
    localStorage.setItem(storageKeys.primaryColor, colorName);
    updatePrimaryPalette({
      50: `{${colorName}.50}`,
      100: `{${colorName}.100}`,
      200: `{${colorName}.200}`,
      300: `{${colorName}.300}`,
      400: `{${colorName}.400}`,
      500: `{${colorName}.500}`,
      600: `{${colorName}.600}`,
      700: `{${colorName}.700}`,
      800: `{${colorName}.800}`,
      900: `{${colorName}.900}`,
      950: `{${colorName}.950}`,
    });
  };

  /**
   * Get current primary color
   */
  const getPrimaryColor = () => {
    return (
      localStorage.getItem(storageKeys.primaryColor) || defaults.primaryColor
    );
  };

  /**
   * Apply surface color palette
   */
  const applySurfaceColor = () => {
    const savedSurfaceColor =
      localStorage.getItem(storageKeys.surfaceColor) || defaults.surfaceColor;
    if (customSurfacePalettes[savedSurfaceColor]) {
      updateSurfacePalette(customSurfacePalettes[savedSurfaceColor]);
    } else {
      updateSurfacePalette({
        0: "#ffffff",
        50: `{${savedSurfaceColor}.50}`,
        100: `{${savedSurfaceColor}.100}`,
        200: `{${savedSurfaceColor}.200}`,
        300: `{${savedSurfaceColor}.300}`,
        400: `{${savedSurfaceColor}.400}`,
        500: `{${savedSurfaceColor}.500}`,
        600: `{${savedSurfaceColor}.600}`,
        700: `{${savedSurfaceColor}.700}`,
        800: `{${savedSurfaceColor}.800}`,
        900: `{${savedSurfaceColor}.900}`,
        950: `{${savedSurfaceColor}.950}`,
      });
    }
    return savedSurfaceColor;
  };

  /**
   * Set surface color and save to localStorage
   */
  const setSurfaceColor = (colorName) => {
    localStorage.setItem(storageKeys.surfaceColor, colorName);
    if (customSurfacePalettes[colorName]) {
      updateSurfacePalette(customSurfacePalettes[colorName]);
    } else {
      updateSurfacePalette({
        0: "#ffffff",
        50: `{${colorName}.50}`,
        100: `{${colorName}.100}`,
        200: `{${colorName}.200}`,
        300: `{${colorName}.300}`,
        400: `{${colorName}.400}`,
        500: `{${colorName}.500}`,
        600: `{${colorName}.600}`,
        700: `{${colorName}.700}`,
        800: `{${colorName}.800}`,
        900: `{${colorName}.900}`,
        950: `{${colorName}.950}`,
      });
    }
  };

  /**
   * Get current surface color
   */
  const getSurfaceColor = () => {
    return (
      localStorage.getItem(storageKeys.surfaceColor) || defaults.surfaceColor
    );
  };

  /**
   * Apply font scale
   */
  const applyScale = () => {
    const savedScale =
      localStorage.getItem(storageKeys.scale) || defaults.scale.toString();
    const scaleIndex = parseInt(savedScale);
    const fontSize = scaleValues[scaleIndex] || scaleValues[defaults.scale];
    document.documentElement.style.fontSize = `${fontSize}px`;
    return scaleIndex;
  };

  /**
   * Set font scale and save to localStorage
   */
  const setScale = (scaleIndex) => {
    const fontSize = scaleValues[scaleIndex];
    if (fontSize) {
      document.documentElement.style.fontSize = `${fontSize}px`;
      localStorage.setItem(storageKeys.scale, scaleIndex.toString());
    }
  };

  /**
   * Get current scale index
   */
  const getScale = () => {
    return parseInt(
      localStorage.getItem(storageKeys.scale) || defaults.scale.toString(),
    );
  };

  /**
   * Apply all theme settings at once
   */
  const applyAllThemeSettings = () => {
    applyDarkMode();
    applyPreset();
    applyPrimaryColor();
    applySurfaceColor();
    applyScale();
  };

  /**
   * Reset all theme settings to defaults
   */
  const resetToDefaults = () => {
    localStorage.removeItem(storageKeys.darkMode);
    localStorage.removeItem(storageKeys.preset);
    localStorage.removeItem(storageKeys.primaryColor);
    localStorage.removeItem(storageKeys.surfaceColor);
    localStorage.removeItem(storageKeys.scale);
    applyAllThemeSettings();
  };

  return {
    // Apply methods
    applyDarkMode,
    applyPreset,
    applyPrimaryColor,
    applySurfaceColor,
    applyScale,
    applyAllThemeSettings,

    // Setters
    setDarkMode,
    setPreset,
    setPrimaryColor,
    setSurfaceColor,
    setScale,

    // Getters
    isDarkMode,
    getPreset,
    getPrimaryColor,
    getSurfaceColor,
    getScale,

    // Reset
    resetToDefaults,

    // Constants
    themePresets,
    primaryColors,
    surfaceColors,
    customSurfacePalettes,
    scaleValues,
    scalePercentages,
    defaults,
    storageKeys,
  };
}
